// src/components/RegisterPage.js

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    useTheme,
    useMediaQuery,
    alpha,
    Fade,
    Zoom,
    Divider,
    Chip
} from '@mui/material';
import {
    School as StudentIcon,
    Work as SupervisorIcon,
    AdminPanelSettings as AdminIcon,
    ArrowForward,
    Psychology,
    MenuBook,
    Analytics,
    TrendingUp,
    People,
    Description,
    VerifiedUser,
    Security,
    CheckCircleOutlineOutlined,
    Login as LoginIcon,
    AccountBalance as InstitutionHeadIcon,
    AccountBalance
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { companyInfo } from '../utils/dataInfo';

// Animation keyframes
const floatAnimation = keyframes`
    0% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    100% { transform: translateY(0px); }
`;

const RegisterCard = styled(Card)(({ theme, color }) => ({
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(3),
    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${color}, ${color}80, transparent)`,
        transform: 'scaleX(0)',
        transformOrigin: 'left',
        transition: 'transform 0.3s ease',
    },
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: `0 20px 40px ${alpha(color, 0.2)}`,
        border: `1px solid ${alpha(color, 0.3)}`,
        '&::before': {
            transform: 'scaleX(1)',
        },
        '& .card-icon': {
            transform: 'scale(1.1) rotate(5deg)',
        },
        '& .arrow-icon': {
            transform: 'translateX(8px)',
        },
        '& .feature-list': {
            opacity: 1,
        }
    }
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
    width: 90,
    height: 90,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    background: `linear-gradient(135deg, ${alpha(color, 0.15)} 0%, ${alpha(color, 0.05)} 100%)`,
    color: color,
    transition: 'all 0.3s ease',
    border: `2px solid ${alpha(color, 0.2)}`,
    '& .MuiSvgIcon-root': {
        fontSize: 48,
        transition: 'all 0.3s ease',
    }
}));

const StatBadge = styled(Box)(({ theme, color }) => ({
    position: 'absolute',
    top: 16,
    right: 16,
    padding: '4px 12px',
    borderRadius: 20,
    background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.8)})`,
    color: 'white',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    boxShadow: `0 2px 8px ${alpha(color, 0.3)}`,
    zIndex: 1
}));

const GradientText = styled(Typography)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
}));

const RegisterPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const currentYear = new Date().getFullYear();

    const userTypes = [
        {
            id: 'student',
            title: 'Researcher',
            description: 'Access research projects, submit proposals, track supervision progress, and collaborate with research supervisors.',
            icon: <StudentIcon />,
            color: '#10B981',
            path: '/student/register',
            gradient: 'linear-gradient(135deg, #10B981, #059669)',
            features: [
                'Research project submission',
                'Supervisor matching',
                'Progress tracking',
                'Collaboration tools'
            ],
            stats: '2,500+ Active Researchers',
            badgeIcon: <People sx={{ fontSize: 12 }} />
        },
        {
            id: 'supervisor',
            title: 'Research Supervisor',
            description: 'Manage supervisees, review research proposals, provide feedback, and oversee research workflow.',
            icon: <SupervisorIcon />,
            color: '#3B82F6',
            path: '/supervisor/register',
            gradient: 'linear-gradient(135deg, #3B82F6, #2563EB)',
            features: [
                'Manage supervisees',
                'Review proposals',
                'Provide feedback',
                'Track progress'
            ],
            stats: '500+ Active Research Supervisors',
            badgeIcon: <TrendingUp sx={{ fontSize: 12 }} />
        },
        {
            id: 'institution_admin',
            title: 'Unit Research Lead',
            description: 'Manage unit research activities, oversee users, generate reports, and ensure compliance at the administrative level.',
            icon: <AdminIcon />,
            color: '#8B5CF6',
            path: '/institution-admin/register',
            gradient: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
            features: [
                'User management',
                'Unit analytics',
                'Research oversight',
                'Compliance tracking'
            ],
            stats: '50+ Units',
            badgeIcon: <VerifiedUser sx={{ fontSize: 12 }} />
        },
        {
            id: 'institution_head',
            title: 'Institutional Research Lead',
            description: 'Provide strategic oversight of institutional research, approve major initiatives, manage resources, and drive research excellence.',
            icon: <InstitutionHeadIcon />,
            color: '#F59E0B',
            path: '/institution-head/register',
            gradient: 'linear-gradient(135deg, #F59E0B, #D97706)',
            features: [
                'Strategic research planning',
                'Resource allocation',
                'Institutional policy oversight',
                'Research excellence initiatives'
            ],
            stats: '100+ Institutional Research Leads',
            badgeIcon: <AccountBalance sx={{ fontSize: 12 }} />
        }
    ];

    const handleRegister = (path) => {
        navigate(path);
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            position: 'relative',
            overflow: 'auto',
            py: isMobile ? 2 : 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {/* Animated Background */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    background: `${theme.palette.common.auth}`,
                }}
            />

            {/* Decorative floating elements */}
            <Box sx={{ position: 'fixed', top: '10%', left: '5%', zIndex: 1, opacity: 0.3, animation: `${floatAnimation} 6s ease-in-out infinite` }}>
                <Psychology sx={{ fontSize: 80, color: '#3B82F6' }} />
            </Box>
            <Box sx={{ position: 'fixed', bottom: '15%', right: '5%', zIndex: 1, opacity: 0.3, animation: `${floatAnimation} 8s ease-in-out infinite` }}>
                <MenuBook sx={{ fontSize: 100, color: '#8B5CF6' }} />
            </Box>

            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, my: 'auto' }}>
                <Fade in={true} timeout={800}>
                    <Box>
                        {/* Header Section */}
                        <Zoom in={true} timeout={500}>
                            <Box sx={{ textAlign: 'center', mb: 5 }}>
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mb: 3,
                                        position: 'relative'
                                    }}>
                                        <Box sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: 120,
                                            height: 120,
                                            background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)',
                                            borderRadius: '50%',
                                            filter: 'blur(20px)'
                                        }} />
                                        <img
                                            src={companyInfo.logo}
                                            alt={`${companyInfo.name} Logo`}
                                            style={{
                                                height: isMobile ? '70px' : '100px',
                                                width: 'auto',
                                                maxWidth: '100%',
                                                objectFit: 'contain',
                                                filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.2))',
                                                position: 'relative',
                                                zIndex: 1
                                            }}
                                        />
                                    </Box>
                                </motion.div>

                                <motion.div
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                >
                                    <Typography
                                        variant={isMobile ? 'h4' : 'h2'}
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 1.5,
                                            fontSize: isMobile ? '2rem' : '3rem',
                                            letterSpacing: '-0.02em'
                                        }}
                                    >
                                        Join{' '}
                                        <GradientText component="span">
                                            SJC ScholarFlow
                                        </GradientText>
                                    </Typography>
                                </motion.div>

                                <motion.div
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            maxWidth: 600,
                                            mx: 'auto',
                                            mb: 1,
                                            color: 'text.secondary',
                                            fontSize: isMobile ? '1rem' : '1.25rem'
                                        }}
                                    >
                                        Choose your account type to get started
                                    </Typography>

                                    {/* Prominent Sign In Redirect Button up top */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center', 
                                        gap: 1, 
                                        mt: 2, 
                                        mb: 3 
                                    }}>
                                        <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                            Already have an account?
                                        </Typography>
                                        <Button
                                            component={Link}
                                            to="/login"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<LoginIcon />}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: 2,
                                                fontWeight: 'bold',
                                                px: 3,
                                                py: 0.8,
                                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`
                                                }
                                            }}
                                        >
                                            Sign In
                                        </Button>
                                    </Box>
                                </motion.div>
                            </Box>
                        </Zoom>

                        {/* Registration Cards - Grid with 4 cards */}
                        <Grid 
                            container 
                            spacing={isMobile ? 2 : 3} 
                            justifyContent="center" 
                            alignItems="stretch"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            {userTypes.map((type, index) => (
                                <Grid 
                                    item 
                                    xs={12} 
                                    sm={6} 
                                    md={3} 
                                    key={type.id} 
                                    sx={{ 
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <motion.div
                                        initial={{ y: 50, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                                        style={{ 
                                            width: '100%', 
                                            display: 'flex',
                                            maxWidth: isMobile ? '100%' : '370px'
                                        }}
                                    >
                                        <RegisterCard
                                            color={type.color}
                                            onClick={() => handleRegister(type.path)}
                                            sx={{
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <StatBadge color={type.color}>
                                                {type.badgeIcon}
                                                {type.stats}
                                            </StatBadge>
                                            
                                            <CardContent sx={{ 
                                                p: isMobile ? 3 : 4, 
                                                flex: 1, 
                                                display: 'flex', 
                                                flexDirection: 'column' 
                                            }}>
                                                <IconWrapper color={type.color} className="card-icon">
                                                    {type.icon}
                                                </IconWrapper>
                                                
                                                <Typography 
                                                    variant="h4" 
                                                    align="center" 
                                                    gutterBottom 
                                                    sx={{ 
                                                        fontWeight: 'bold', 
                                                        mb: 2,
                                                        fontSize: isMobile ? '1.5rem' : '1.8rem',
                                                        background: type.gradient,
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent'
                                                    }}
                                                >
                                                    {type.title}
                                                </Typography>
                                                
                                                <Typography 
                                                    variant="body2" 
                                                    align="center" 
                                                    sx={{ 
                                                        mb: 3, 
                                                        minHeight: isMobile ? 'auto' : 80,
                                                        color: alpha(theme.palette.text.primary, 0.75),
                                                        lineHeight: 1.6
                                                    }}
                                                >
                                                    {type.description}
                                                </Typography>

                                                <Divider sx={{ mb: 2.5 }}>
                                                    <Chip 
                                                        label="Key Benefits" 
                                                        size="small" 
                                                        sx={{ 
                                                            backgroundColor: alpha(type.color, 0.1),
                                                            color: type.color,
                                                            fontWeight: 'bold'
                                                        }} 
                                                    />
                                                </Divider>

                                                <Box sx={{ mb: 3.5, flex: 1 }}>
                                                    {type.features.map((feature, idx) => (
                                                        <Box key={idx} sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: 1.5, 
                                                            mb: 1.5,
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            <CheckCircleOutlineOutlined sx={{ 
                                                                fontSize: 18, 
                                                                color: type.color,
                                                                flexShrink: 0
                                                            }} />
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    color: alpha(theme.palette.text.primary, 0.85),
                                                                    lineHeight: 1.4,
                                                                    wordBreak: 'break-word'
                                                                }}
                                                            >
                                                                {feature}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>

                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    endIcon={<ArrowForward className="arrow-icon" />}
                                                    onClick={() => handleRegister(type.path)}
                                                    sx={{
                                                        background: type.gradient,
                                                        textTransform: 'none',
                                                        fontWeight: 'bold',
                                                        py: 1.5,
                                                        borderRadius: 2,
                                                        transition: 'all 0.3s ease',
                                                        fontSize: '1rem',
                                                        mt: 'auto',
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: `0 8px 20px ${alpha(type.color, 0.4)}`
                                                        }
                                                    }}
                                                >
                                                    Register as {type.title}
                                                </Button>
                                            </CardContent>
                                        </RegisterCard>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Footer */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.6 }}
                        >
                            <Box sx={{ 
                                textAlign: 'center', 
                                mt: 6, 
                                pt: 4,
                                borderTop: `1px solid ${alpha(theme.palette.common.white, 0.1)}`
                            }}>
                                <Box sx={{ 
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: isMobile ? 2 : 3,
                                    flexWrap: 'wrap',
                                    mb: 3
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Security sx={{ fontSize: 18, color: '#10B981' }} />
                                        <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 500 }}>
                                            Secure Platform
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Description sx={{ fontSize: 18, color: '#3B82F6' }} />
                                        <Typography variant="caption" sx={{ color: '#3B82F6', fontWeight: 500 }}>
                                            Research Management
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Analytics sx={{ fontSize: 18, color: '#8B5CF6' }} />
                                        <Typography variant="caption" sx={{ color: '#8B5CF6', fontWeight: 500 }}>
                                            Analytics Dashboard
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccountBalance sx={{ fontSize: 18, color: '#F59E0B' }} />
                                        <Typography variant="caption" sx={{ color: '#F59E0B', fontWeight: 500 }}>
                                            Institutional Leadership
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Need help? Please contact support or visit the registration guide.
                                    </Typography>
                                </Box>

                                <Typography variant="caption" sx={{ 
                                    display: 'block', 
                                    mt: 3,
                                    color: 'text.secondary',
                                    opacity: 0.8
                                }}>
                                    © {currentYear} SJC ScholarFlow. All rights reserved.
                                </Typography>
                            </Box>
                        </motion.div>
                    </Box>
                </Fade>
            </Container>
        </Box>
    );
};

export default RegisterPage;